/* eslint-disable max-lines */
import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
    X
} from 'lucide-react';
import './tokens.css';

/**
 * DE Studio UI primitives — token-driven, scoped via [data-theme="destudio"].
 *
 * Use these inside any DE Studio surface. They consume `--ds-*` tokens; rest
 * of the platform never sees them. Style is intentionally minimal — class
 * names from `tokens.css` carry the visual weight; props stay plain HTML-ish.
 */

const cx = (...xs) => xs.filter(Boolean).join(' ');

/* ---------------- Button ---------------- */
export const DSButton = React.forwardRef(function DSButton(
    {
        variant = 'primary', size = 'md', className, type = 'button', children, ...rest
    },
    ref
) {
    const variantClass = {
        primary:   'ds-button-primary',
        secondary: 'ds-button-secondary',
        ghost:     'ds-button-ghost',
        danger:    'ds-button-danger'
    }[variant] || 'ds-button-primary';

    const sizeClass = size === 'sm' ? 'ds-button-sm' : size === 'lg' ? 'ds-button-lg' : '';

    return (
        <button
            ref={ref}
            type={type}
            className={cx('ds-button', variantClass, sizeClass, className)}
            {...rest}
        >
            {children}
        </button>
    );
});

/* ---------------- Input / Textarea ---------------- */
export const DSInput = React.forwardRef(function DSInput(
    {
        mono = false, className, ...rest
    }, ref
) {
    return (
        <input
            ref={ref}
            className={cx('ds-input', mono && 'ds-input-mono', className)}
            {...rest}
        />
    );
});

export const DSTextarea = React.forwardRef(function DSTextarea(
    {
        mono = false, className, rows = 4, ...rest
    }, ref
) {
    return (
        <textarea
            ref={ref}
            rows={rows}
            className={cx('ds-textarea', mono && 'ds-input-mono', className)}
            {...rest}
        />
    );
});

/* ---------------- Label / Field ---------------- */
export function DSLabel({
    children, htmlFor, required, optional, className
}) {
    return (
        <label htmlFor={htmlFor} className={cx('ds-label', className)}>
            {children}
            {required && <span style={{
                color: 'var(--ds-danger)',
                marginLeft: 4
            }}>*</span>}
            {optional && (
                <span style={{
                    color: 'var(--ds-text-subtle)',
                    fontWeight: 400,
                    marginLeft: 6
                }}>
                    optional
                </span>
            )}
        </label>
    );
}

export function DSField({
    label, helpText, error, required, optional, htmlFor, children, className
}) {
    return (
        <div className={cx(className)}>
            {label && <DSLabel htmlFor={htmlFor} required={required} optional={optional}>{label}</DSLabel>}
            {children}
            {error
                ? <div className="ds-error">{error}</div>
                : helpText
                    ? <div className="ds-help">{helpText}</div>
                    : null}
        </div>
    );
}

/* ---------------- Card / Section ---------------- */
export function DSCard({
    flat = false, className, style, children, onClick, role, tabIndex, as = 'div'
}) {
    const Tag = as;
    return (
        <Tag
            className={cx(flat ? 'ds-card-flat' : 'ds-card', className)}
            style={style}
            onClick={onClick}
            role={role}
            tabIndex={tabIndex}
        >
            {children}
        </Tag>
    );
}

export function DSSection({
    title, action, children, className
}) {
    return (
        <section className={cx('ds-section', className)}>
            {(title || action) && (
                <div className="ds-section-header">
                    <div style={{
                        flex: 1,
                        minWidth: 0
                    }}>
                        {typeof title === 'string'
                            ? <span style={{
                                fontWeight: 600,
                                fontSize: 'var(--ds-text-md)'
                            }}>{title}</span>
                            : title}
                    </div>
                    {action}
                </div>
            )}
            <div className="ds-section-body">{children}</div>
        </section>
    );
}

/* ---------------- Badge ---------------- */
export function DSBadge({
    tone = 'neutral', children, className
}) {
    return (
        <span className={cx('ds-badge', `ds-badge-${tone}`, className)}>{children}</span>
    );
}

/* ---------------- Dialog (Radix) ---------------- */
export const DSDialog = DialogPrimitive.Root;
export const DSDialogTrigger = DialogPrimitive.Trigger;

export function DSDialogContent({
    children, className, hideClose = false, title, description, footer, size = 'md'
}) {
    const widthMap = {
        sm: 480,
        md: 640,
        lg: 880,
        xl: 1080
    };
    return (
        <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="ds-dialog-overlay" />
            <DialogPrimitive.Content
                className={cx('ds-dialog-content', className)}
                style={{
                    width: `min(94vw, ${widthMap[size] || widthMap.md}px)`
                }}
            >
                {(title || description || !hideClose) && (
                    <div style={{
                        padding: '20px 24px 16px',
                        borderBottom: '1px solid var(--ds-divider)',
                        position: 'relative'
                    }}>
                        {title && (
                            <DialogPrimitive.Title style={{
                                fontSize: 'var(--ds-text-lg)',
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                margin: 0,
                                color: 'var(--ds-text)'
                            }}>
                                {title}
                            </DialogPrimitive.Title>
                        )}
                        {description && (
                            <DialogPrimitive.Description style={{
                                margin: '6px 0 0',
                                fontSize: 'var(--ds-text-sm)',
                                color: 'var(--ds-text-muted)',
                                lineHeight: 1.55
                            }}>
                                {description}
                            </DialogPrimitive.Description>
                        )}
                        {!hideClose && (
                            <DialogPrimitive.Close
                                aria-label="Close"
                                style={{
                                    position: 'absolute',
                                    top: 14,
                                    right: 14,
                                    width: 32,
                                    height: 32,
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--ds-text-muted)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 'var(--ds-radius-sm)'
                                }}
                            >
                                <X size={16} />
                            </DialogPrimitive.Close>
                        )}
                    </div>
                )}
                <div style={{
                    padding: '20px 24px'
                }}>{children}</div>
                {footer && (
                    <div style={{
                        padding: '14px 24px',
                        borderTop: '1px solid var(--ds-divider)',
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'flex-end',
                        background: 'var(--ds-surface-2)'
                    }}>
                        {footer}
                    </div>
                )}
            </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
    );
}

/* ---------------- Empty state ---------------- */
export function DSEmptyState({
    icon: Icon, title, description, action, className
}) {
    return (
        <div
            className={cx(className)}
            style={{
                textAlign: 'center',
                padding: '48px 24px',
                border: '1px dashed var(--ds-border-strong)',
                borderRadius: 'var(--ds-radius-lg)',
                background: 'var(--ds-surface)'
            }}
        >
            {Icon && (
                <div style={{
                    width: 56,
                    height: 56,
                    margin: '0 auto 16px',
                    background: 'var(--ds-primary-soft)',
                    color: 'var(--ds-primary)',
                    borderRadius: 'var(--ds-radius-lg)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Icon size={22} />
                </div>
            )}
            {title && <div className="ds-h4" style={{
                marginBottom: 4
            }}>{title}</div>}
            {description && (
                <div className="ds-body" style={{
                    maxWidth: 460,
                    margin: '0 auto',
                    color: 'var(--ds-text-muted)'
                }}>
                    {description}
                </div>
            )}
            {action && <div style={{
                marginTop: 18
            }}>{action}</div>}
        </div>
    );
}

/* ---------------- Stack helpers (for layout uniformity) ---------------- */
export function DSStack({
    gap = 4, direction = 'col', children, className, style, align, justify
}) {
    return (
        <div
            className={className}
            style={{
                display: 'flex',
                flexDirection: direction === 'row' ? 'row' : 'column',
                gap: `var(--ds-space-${gap})`,
                alignItems: align,
                justifyContent: justify,
                ...style
            }}
        >
            {children}
        </div>
    );
}
